import React, { useState } from 'react';
import { Scale } from 'lucide-react';
import { SEO } from '../components/SEO';

interface WeightState {
  profileType: 'square' | 'rectangular' | 'round' | 'hollow-round' | 'plate' | 'flat' | 'beam';
  dimension: string;
  thickness: string;
  length: string;
  weightPerMeter: number | null;
  totalWeight: number | null;
}

function KalkulatorTezine() {
  const [values, setValues] = useState<WeightState>({
    profileType: 'square',
    dimension: '',
    thickness: '',
    length: '',
    weightPerMeter: null,
    totalWeight: null
  });

  // Density of steel in kg/m³
  const STEEL_DENSITY = 7850;

  const calculateWeight = () => {
    const dimension = parseFloat(values.dimension);
    const thickness = parseFloat(values.thickness);
    const length = parseFloat(values.length);

    if (isNaN(dimension) || isNaN(thickness) || isNaN(length)) {
      return;
    }

    let area = 0; // Cross-sectional area in m²
    
    switch (values.profileType) {
      case 'square':
        // Square tube
        area = (dimension * dimension - (dimension - 2 * thickness) * (dimension - 2 * thickness)) / 1000000;
        break;
      case 'rectangular':
        // Rectangular tube
        area = (dimension * dimension - (dimension - 2 * thickness) * (dimension - 2 * thickness)) / 1000000;
        break;
      case 'round':
        // Round tube
        const outerRadius = dimension / 2;
        const innerRadius = (dimension - 2 * thickness) / 2;
        area = Math.PI * (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2)) / 1000000;
        break;
      case 'hollow-round':
        // Hollow round tube
        area = Math.PI * Math.pow(dimension / 2, 2) / 1000000;
        break;
      case 'plate':
        // Steel plate
        area = (dimension * thickness) / 1000000;
        break;
      case 'flat':
        // Flat steel
        area = (dimension * thickness) / 1000000;
        break;
      case 'beam':
        // I-beam (simplified calculation)
        area = ((dimension * thickness) + (dimension * thickness * 2)) / 1000000;
        break;
    }

    const weightPerMeter = area * STEEL_DENSITY;
    const totalWeight = weightPerMeter * length;

    setValues(prev => ({
      ...prev,
      weightPerMeter,
      totalWeight
    }));
  };

  const handleInputChange = (field: keyof WeightState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
      weightPerMeter: null,
      totalWeight: null
    }));
  };

  const formatWeight = (weight: number | null): string => {
    if (weight === null) return '-';
    return weight.toFixed(2);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Težine",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte težinu čeličnih profila. Podržava različite vrste profila i dimenzije.",
    "url": "https://online-kalkulator.com/kalkulator-tezine",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Težine Metala | Izračun Težine Čeličnih Profila"
        description="Izračunajte težinu različitih čeličnih profila. Podržava kvadratne, pravokutne, okrugle cijevi i druge profile."
        canonicalUrl="/kalkulator-tezine"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Scale className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Težine Metala i Cijevi</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Profile Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vrsta profila
                </label>
                <select
                  value={values.profileType}
                  onChange={(e) => handleInputChange('profileType', e.target.value as WeightState['profileType'])}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="square">Kvadratne cijevi</option>
                  <option value="rectangular">Pravokutne cijevi</option>
                  <option value="round">Bešavne čelične cijevi</option>
                  <option value="hollow-round">Šavne okrugle cijevi</option>
                  <option value="plate">Čelični lim</option>
                  <option value="flat">Čelik</option>
                  <option value="beam">Čelični nosači</option>
                </select>
              </div>

              {/* Dimensions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    A-Dimenzija (mm)
                  </label>
                  <input
                    type="number"
                    value={values.dimension}
                    onChange={(e) => handleInputChange('dimension', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    t-Debljina stijenke (mm)
                  </label>
                  <input
                    type="number"
                    value={values.thickness}
                    onChange={(e) => handleInputChange('thickness', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 2"
                  />
                </div>
              </div>

              {/* Length */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dužina (m)
                </label>
                <input
                  type="number"
                  value={values.length}
                  onChange={(e) => handleInputChange('length', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="npr. 6"
                />
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculateWeight}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {values.weightPerMeter !== null && values.totalWeight !== null && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Težina (kg/m):</span>
                    <span className="text-lg font-semibold">
                      {formatWeight(values.weightPerMeter)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Ukupna težina (kg):</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatWeight(values.totalWeight)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Težine Metala i Cijevi</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator težine omogućuje vam izračun težine različitih čeličnih profila na temelju
                  njihovih dimenzija. Podržava različite vrste profila kao što su kvadratne i pravokutne
                  cijevi, bešavne cijevi, čelični lim i nosači.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Podržani Profili</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Kvadratne cijevi</li>
                  <li>Pravokutne cijevi</li>
                  <li>Bešavne čelične cijevi</li>
                  <li>Šavne okrugle cijevi</li>
                  <li>Čelični lim</li>
                  <li>Čelik</li>
                  <li>Čelični nosači</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite vrstu profila</li>
                  <li>Unesite A-dimenziju u milimetrima</li>
                  <li>Unesite debljinu stijenke u milimetrima</li>
                  <li>Unesite željenu dužinu u metrima</li>
                  <li>Kliknite "Izračunaj" za rezultat</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Svi izračuni su bazirani na gustoći čelika od 7850 kg/m³</li>
                  <li>Dimenzije se unose u milimetrima (mm)</li>
                  <li>Dužina se unosi u metrima (m)</li>
                  <li>Rezultati su zaokruženi na dvije decimale</li>
                  <li>Za preciznije izračune konzultirajte tehničke specifikacije proizvođača</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorTezine;